import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Accordion,
  Stack,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import CompareOutlinedIcon from '@mui/icons-material/CompareOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ApexCharts from 'src/components/apexcharts/ApexCharts';
import { useParams } from 'react-router';
import { hasSkillsData } from 'src/utils/skilldar';
import ModalComponent from '../Modal/Modal';
import RadarChartComponent from '../RadarChart/RadarChartComponent';
import DisclaimerInfo from './DisclaimerInfo';
import InfoTip from './InfoTip';
import CustomScrollbar from './CustomScrollbar';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  const compareCareers = useSelector((state) => state.mainLayout.compareCareers);
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function DekstopPoup({
  isLoading,
  loadError,
  onRetry,
  regionData,
  children,
  tabValue,
  setTabValue,
  careerNCoursesPopDetails,
  openCareerDetailsPopup,
  HandleCloseCareerDetailsPopup,
  showComparePopup,
  addToCompare,
  compareCareersState,
  getSkillsReport,
  addToAnalytics,
  redirectUrl,
  currentNGoalCareerId,
  expanded,
  HandleOpenRadarPopup,
  regionalChartData,
  qualificationsChartData,
  handleChange,
  isWidget,
  hideColleges,
  hideGrowth,
}) {
  const darkBlue = '#000c3b';
  const wrapperRef = useRef(null);
  const [value, setChangeValue] = useState(0);
  const handleChangeTab = (event, newValue) => {
    setChangeValue(newValue);
  };
  const { rg_name } = useParams();
  // Called when tab is switched
  const handleRegionTab = (event, newValue) => {
    setTabValue(newValue);
  };
  const colorTheme = useTheme();
  const buttonColor = regionData?.button?.bgColor;
  const buttonFontColor = regionData?.button?.color;
  const logo = regionData?.logo || '';
  const partnerLogos = regionData?.partnerLogos || [];
  const primaryColor = regionData?.primaryColor || colorTheme?.palette.primary.main;
  const fontColor = regionData?.fontColor || '';
  const secondaryColor = regionData?.secondaryColor || colorTheme?.palette?.secondary?.main;

  const steps = useMemo(() => ['National', regionData?.name], [regionData?.name]);
  const [currentStep, setCurrentStep] = useState(0);
  const onStepChange = (stepIndex) => {
    setTabValue(stepIndex);
  };
  const safeFormatNumber = (currValue) => {
    const number = Number(currValue);
    return Number.isFinite(number) ? number.toFixed(2) : 'N/A';
  };

  useEffect(() => {
    if (tabValue === 1 && value === 2) {
      setChangeValue(0);
    }
  }, [tabValue, value]);

  return (
    <ModalComponent
      open={openCareerDetailsPopup}
      handleClose={HandleCloseCareerDetailsPopup}
      className={`app-popup compare-career careerDetails dekstop custom-scroll${isWidget ? ' tsw-wm' : ''}`}
    >
      <Box
        ref={wrapperRef}
        sx={{
          backgroundColor: `${primaryColor} !important`,
          // the widget's career window colours its section bars from the college's button colours
          ...(isWidget ? { '--tsw-accent': buttonColor || '#f9a748', '--tsw-ink': buttonFontColor || '#000' } : {}),
        }}
        className="popup-wrapper"
      >
        <Box className="popup-head">
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <Button className="close-icon-btn" onClick={HandleCloseCareerDetailsPopup}>
              <CloseIcon
                fontSize="small"
                className="close-career-popup"
                sx={{ bgcolor: 'white', color: 'black !important', borderRadius: '50%' }}
              />
            </Button>
            <Typography
              variant="h1"
              sx={{
                color: buttonColor || darkBlue,
                fontWeight: '700',
                ml: 2,
                fontSize: '2rem !important',
              }}
            >
              {careerNCoursesPopDetails?.title}
            </Typography>
            {careerNCoursesPopDetails && <DisclaimerInfo color={buttonColor || 'white'} />}
          </Box>
          {!hideGrowth && (tabValue || tabValue === 0) && (
            <Tabs
              value={tabValue}
              onChange={handleRegionTab}
              variant="standard"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                display: 'flex',
                gap: 0,
                backgroundColor: primaryColor,
                px: 0,
                py: 0.5,
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', width: '100%' }}>
                {steps?.map((label, index) => {
                  let borderRadius = '0';
                  if (index === 0 && steps.length === 1) {
                    borderRadius = '8px';
                  } else if (index === 0) {
                    borderRadius = '8px 0 0 8px';
                  } else if (index === steps.length - 1) {
                    borderRadius = '0 8px 8px 0';
                  }
                  return (
                    <Button
                      key={label}
                      onClick={() => onStepChange(index)}
                      size="small"
                      sx={{
                        flex: 1,
                        borderRadius,
                        textTransform: 'none',
                        backgroundColor: tabValue === index ? buttonColor : primaryColor,
                        color: tabValue === index ? buttonFontColor : buttonColor,
                        fontWeight: tabValue === index ? 700 : 400,
                        border: `1px solid ${buttonColor}`,
                        borderRight:
                          index !== steps.length - 1 ? 'none' : `1px solid ${buttonColor}`,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          backgroundColor: tabValue === index ? buttonColor : primaryColor,
                        },
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </Box>
            </Tabs>
          )}
          {/* {careerNCoursesPopDetails ?
                        <Box>
                            {currentNGoalCareerId.currentRoleId === careerNCoursesPopDetails.id || currentNGoalCareerId.careerGoalId === careerNCoursesPopDetails.id ?
                                ""
                                :

                                <>
                                    <Button className='career-popup-head-btn' sx={{ backgroundColor: (theme) => `${theme.palette.secondary.main} !important` }}
                                        onClick={() => showComparePopup(careerNCoursesPopDetails.id)}
                                    ><Typography sx={{ textTransform: 'none !important' }} variant='button2' > Compare Careers</Typography><CompareOutlinedIcon sx={{ ml: 3 }} /> </Button>
                                    <Button className='career-popup-head-btn' sx={{ backgroundColor: (theme) => `${theme.palette.secondary.main} !important` }} onClick={() => addToCompare(careerNCoursesPopDetails.id)} ><Typography sx={{ textTransform: 'none !important' }} variant='button2' >{compareCareersState.find(currentCareer => currentCareer === careerNCoursesPopDetails.id) ? "Added" : 'Add to Comparison'}</Typography><CompareOutlinedIcon sx={{ ml: 3 }} /> </Button>
                                </>
                            }
                            <Button onClick={() => getSkillsReport('careers-result-page')} className='career-popup-head-btn' sx={{ backgroundColor: (theme) => `${theme.palette.secondary.main} !important` }}><Typography sx={{ textTransform: 'none !important' }} variant='button2' >Get Skill Report</Typography><FileDownloadOutlinedIcon sx={{ ml: 3 }} /> </Button>
                        </Box>
                        : ''
                    } */}
        </Box>
        {careerNCoursesPopDetails && !isLoading ? (
          <Box className="content-wrapper">
            <Box className="row" sx={{ bgcolor: `${primaryColor} !important` }}>
              <Box sx={{ border: `1px solid ${primaryColor}` }} className="desc-head">
                {/* <Typography variant='capitalize' className='heading' color='primary.black'>Description</Typography> */}
                <Typography
                  className="description"
                  sx={{
                    backgroundColor: (theme) =>
                      `${buttonColor || theme.palette.primary.main} !important`,
                    borderBottom: `3px solid ${primaryColor} !important`,
                    marginBottom: '0px !important',
                    fontSize: '1.5rem !important',
                  }}
                  variant="h2"
                  color={buttonFontColor || 'white'}
                >
                  Job Description
                </Typography>
                <Typography
                  component="p"
                  variant="body"
                  color="primary.dark"
                  textAlign="center"
                  sx={{ padding: '10px 20px' }}
                >
                  {careerNCoursesPopDetails?.description || 'No Data Available'}
                </Typography>
              </Box>
              <Box
                className="col courses-wrapper"
                //  sx={{overflow:'hidden'}}
                sx={{
                  display: hideColleges ? 'none !important' : undefined,
                  overflowY: careerNCoursesPopDetails?.courses.length > 9 ? 'scroll' : 'hidden',
                }}
              >
                <Typography
                  className="heading"
                  variant="h2"
                  sx={{
                    backgroundColor: (theme) =>
                      `${buttonColor || theme.palette.primary.main} !important`,
                    borderBottom: `3px solid ${primaryColor} !important`,
                    marginBottom: '0px !important',
                    fontSize: '1.5rem !important',
                  }}
                  color={buttonFontColor || 'white'}
                >
                  Colleges with supporting Courses
                </Typography>
                {careerNCoursesPopDetails?.courses.length ? (
                  <>
                    {careerNCoursesPopDetails.courses.some((item) => item.collegeName)
                      ? // Region format: array of colleges
                        careerNCoursesPopDetails.courses.map((college, collegeIndex) => (
                          <Accordion
                            key={collegeIndex}
                            elevation={1}
                            sx={{
                              marginTop: '0px !important',
                              marginBottom: '0px !important',
                              '&:not(:last-child)': {
                                borderBottom: 'none',
                              }, // Add spacing between colleges
                              backgroundColor: '#f8f9fa', // Subtle background
                              borderRadius: '4px',
                              border: '1px solid #e9ecef',
                              '&:before': { display: 'none' }, // Remove default MUI border
                              boxShadow: 'none !important',
                              // '&:before': { display: 'none !important' },
                              '&:after': { display: 'none !important' },
                            }}
                          >
                            <AccordionSummary
                              sx={{
                                // borderBottom: `1px solid ${colorTheme.palette.divider}`,
                                bgcolor: colorTheme.palette.grey[100],
                                '& .MuiTypography-root': {
                                  fontWeight: 700,
                                },
                              }}
                              expandIcon={<KeyboardArrowRightOutlinedIcon />}
                            >
                              <Typography
                                variant="h3"
                                color="text.primary"
                                sx={{
                                  padding: '10px 8px',
                                  fontSize: '1.1rem !important',
                                }}
                              >
                                {college.collegeName}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '0 0 10px 0', borderBottom: 'none' }}>
                              {college.courses?.map((course, courseIndex) => (
                                <Accordion
                                  key={courseIndex}
                                  expanded={expanded === `${collegeIndex}-${courseIndex}`}
                                  onChange={handleChange(`${collegeIndex}-${courseIndex}`)}
                                  sx={{
                                    marginLeft: '16px',
                                    marginBottom: '4px !important',
                                    backgroundColor: 'white',
                                    borderLeft: '3px solid #dee2e6',
                                    borderRadius: '0 4px 4px 0',
                                  }}
                                >
                                  <AccordionSummary expandIcon={<KeyboardArrowRightOutlinedIcon />}>
                                    <Typography
                                      variant="h3"
                                      color="primary.dark"
                                      sx={{
                                        fontWeight: 600,
                                        padding: '10px 16px',
                                        fontSize: '0.95rem !important',
                                      }}
                                    >
                                      {course.title}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{
                                      pb: 0,
                                      transition: '1ms ease-out',
                                      transitionDelay: '0.1ms',
                                      visibility:
                                        expanded !== `${collegeIndex}-${courseIndex}`
                                          ? 'hidden'
                                          : 'visible',
                                    }}
                                  >
                                    <Typography
                                      component="p"
                                      variant="medium"
                                      sx={{ padding: '0px 16px' }}
                                      className="flex-around"
                                      color="primary.dark"
                                    >
                                      <span style={{ fontWeight: 700 }}>Level</span>
                                      <span style={{ marginLeft: '14px', fontWeight: 400 }}>
                                        {course.level}
                                      </span>
                                    </Typography>

                                    <Typography
                                      component="p"
                                      variant="medium"
                                      color="primary.dark"
                                      sx={{ padding: '0px 16px', margin: '10px 0' }}
                                    >
                                      {course.description}
                                    </Typography>
                                    {course.campuses?.map((campus, campusIndex) => (
                                      <Box
                                        key={campusIndex}
                                        className="highlighted"
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          backgroundColor: '#f1f3f4',
                                          borderRadius: '4px',
                                          margin: '8px 0',
                                          border: '1px solid #dee2e6',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            ml: 2,
                                            mr: 1,
                                            pt: 1,
                                          }}
                                        >
                                          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                                            <Typography
                                              component="p"
                                              sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal',
                                                overflow: 'visible',
                                                textOverflow: 'clip',
                                              }}
                                              variant="medium"
                                              className="flex-around"
                                              color="primary.dark"
                                            >
                                              <span
                                                style={{
                                                  fontWeight: 700,
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                Duration:
                                              </span>
                                              <span
                                                style={{
                                                  marginLeft: '10px',
                                                  fontWeight: 400,
                                                  wordWrap: 'break-word',
                                                  whiteSpace: 'normal',
                                                  flex: 1,
                                                  minWidth: 0,
                                                  overflowWrap: 'break-word',
                                                  wordBreak: 'break-word',
                                                  overflow: 'visible',
                                                  textOverflow: 'clip',
                                                }}
                                              >
                                                {course?.duration || '2 Years - Full Time'}
                                              </span>
                                            </Typography>
                                            <Typography
                                              component="p"
                                              sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                              }}
                                              variant="medium"
                                              className="flex-around"
                                              color="primary.dark"
                                            >
                                              <span style={{ fontWeight: 700 }}>Campus:</span>
                                              <span style={{ marginLeft: '10px', fontWeight: 400 }}>
                                                {campus.name || '-'}
                                              </span>
                                            </Typography>
                                          </Stack>
                                        </Box>
                                        <Box
                                          className="career-responsive-btns"
                                          sx={{ flexShrink: 0 }}
                                        >
                                          <Button
                                            onClick={() =>
                                              addToAnalytics({
                                                event: 'COURSE_ENQUIRY',
                                                course: course.id,
                                              })
                                            }
                                            disabled={!campus.enquiryURL && true}
                                            sx={{
                                              minWidth: '80px',
                                              backgroundColor: colorTheme.palette.secondary.main,
                                              color: '#fff',
                                              borderRadius: 0,
                                              borderRight: `1px solid ${colorTheme.palette.grey[300]}`,
                                              height: '100%',
                                              '&:disabled': {
                                                backgroundColor: colorTheme.palette.grey[300],
                                                color: colorTheme.palette.text.disabled,
                                              },
                                            }}
                                          >
                                            {redirectUrl(campus.enquiryURL, 'Enquire')}
                                          </Button>
                                          <Button
                                            onClick={() =>
                                              addToAnalytics({
                                                event: 'COURSE_APPLY',
                                                course: course.id,
                                              })
                                            }
                                            disabled={!campus.applyURL}
                                            sx={{
                                              minWidth: '80px',
                                              backgroundColor: colorTheme.palette.secondary.main,
                                              color: '#fff',
                                              borderRadius: 0,
                                              borderRight: `1px solid ${colorTheme.palette.grey[300]}`,
                                              height: '100%',
                                              '&:disabled': {
                                                backgroundColor: colorTheme.palette.grey[300],
                                                color: colorTheme.palette.text.disabled,
                                              },
                                            }}
                                          >
                                            {redirectUrl(campus.applyURL, 'Apply')}
                                          </Button>
                                          <Button
                                            onClick={() =>
                                              addToAnalytics({
                                                event: 'COURSE_DETAILS',
                                                course: course.id,
                                              })
                                            }
                                            disabled={!campus.pageURL}
                                            sx={{
                                              minWidth: '80px',
                                              backgroundColor: colorTheme.palette.secondary.main,
                                              color: '#fff',
                                              borderRadius: 0,
                                              borderRight: `1px solid ${colorTheme.palette.grey[300]}`,
                                              height: '100%',
                                              '&:disabled': {
                                                backgroundColor: colorTheme.palette.grey[300],
                                                color: colorTheme.palette.text.disabled,
                                              },
                                            }}
                                          >
                                            {redirectUrl(campus.pageURL, 'More Info')}
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        ))
                      : // Original format: direct courses array
                        careerNCoursesPopDetails?.courses?.map((item, index) => (
                          <Accordion
                            sx={{
                              marginBottom: '0px !important',
                              border: 'none',
                              borderBottom: `3px solid ${primaryColor} !important`,
                            }}
                            expanded={expanded === index}
                            onChange={handleChange(index)}
                            key={index}
                          >
                            <AccordionSummary
                              expandIcon={
                                <KeyboardArrowRightOutlinedIcon
                                  sx={{
                                    color: 'black',
                                    fontWeight: 'bold',
                                    fontSize: '2.5rem',
                                  }}
                                />
                              }
                              aria-controls="supporting-courses"
                              id={index}
                            >
                              <Typography
                                variant="body"
                                color="primary.dark"
                                sx={{ fontWeight: 700, padding: '8px 16px' }}
                              >
                                {item.title}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                pb: 0,
                                transition: '1ms ease-out',
                                transitionDelay: '0.1ms',
                                visibility: expanded !== index ? 'hidden' : 'visible',
                              }}
                            >
                              <Typography
                                component="p"
                                variant="medium"
                                sx={{ padding: '0px 16px' }}
                                className="flex-around"
                                color="primary.dark"
                              >
                                <span style={{ fontWeight: 700 }}>Level</span>
                                <span style={{ marginLeft: '14px', fontWeight: 400 }}>
                                  {item.level}
                                </span>
                              </Typography>

                              <Typography
                                component="p"
                                variant="medium"
                                color="primary.dark"
                                sx={{ padding: '0px 16px', margin: '10px 0' }}
                              >
                                {item.description}
                              </Typography>
                              {item?.campus?.length &&
                                item?.campus?.map((campus, i) => (
                                  <Box
                                    key={i}
                                    className="highlighted"
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      marginTop: '2px',
                                      bgcolor: '#dfdfdf',
                                      p: 0,
                                    }}
                                  >
                                    <Stack direction="row" sx={{ ml: 2 }}>
                                      <Typography
                                        component="p"
                                        sx={{
                                          minWidth: '30%',
                                        }}
                                        variant="medium"
                                        className="flex-around"
                                        color="primary.dark"
                                      >
                                        <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                          Duration:
                                        </span>
                                        <span
                                          style={{
                                            marginLeft: '10px',
                                            fontWeight: 400,
                                          }}
                                        >
                                          {item?.duration || '2 Years - Full Time'}
                                        </span>
                                      </Typography>
                                      <Typography
                                        component="p"
                                        sx={{ minWidth: '30%', ml: 5 }}
                                        variant="medium"
                                        className="flex-around"
                                        color="primary.dark"
                                      >
                                        <span style={{ fontWeight: 700 }}>Campus:</span>
                                        <span style={{ marginLeft: '10px', fontWeight: 400 }}>
                                          {campus.name || '-'}
                                        </span>
                                      </Typography>
                                    </Stack>
                                    <Box className="career-responsive-btns">
                                      <Button
                                        onClick={() =>
                                          addToAnalytics({
                                            event: 'COURSE_ENQUIRY',
                                            course: item?.id,
                                          })
                                        }
                                        disabled={!campus.enquiryURL && true}
                                        sx={{
                                          minWidth: '10%',
                                          backgroundColor: (theme) =>
                                            `${theme.palette.secondary.main} !important`,
                                          borderRadius: '0px',
                                          borderRight: '2px solid white',
                                          padding: '5px,15px',
                                          height: '100%',
                                        }}
                                      >
                                        {redirectUrl(campus.enquiryURL, 'Enquire')}
                                      </Button>
                                      <Button
                                        onClick={() =>
                                          addToAnalytics({
                                            event: 'COURSE_APPLY',
                                            course: item?.id,
                                          })
                                        }
                                        disabled={!campus.applyURL && true}
                                        sx={{
                                          minWidth: '10%',
                                          backgroundColor: (theme) =>
                                            `${theme.palette.secondary.main} !important`,
                                          borderRadius: '0px',
                                          borderRight: '2px solid #dfdfdf',
                                          padding: '5px,15px',
                                          height: '100%',
                                        }}
                                      >
                                        {redirectUrl(campus.applyURL, 'Apply')}
                                      </Button>
                                      <Button
                                        onClick={() =>
                                          addToAnalytics({
                                            event: 'COURSE_DETAILS',
                                            course: item?.id,
                                          })
                                        }
                                        disabled={!campus.pageURL && true}
                                        sx={{
                                          minWidth: '10%',
                                          backgroundColor: (theme) =>
                                            `${theme.palette.secondary.main} !important`,
                                          borderRadius: '0px',
                                          padding: '5px,15px',
                                          height: '100%',
                                        }}
                                      >
                                        {redirectUrl(campus.pageURL, 'More Info')}
                                      </Button>
                                    </Box>
                                  </Box>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                  </>
                ) : (
                  <Typography
                    component="p"
                    sx={{ backgroundColor: '#f4f4f4', p: 1, fontSize: '1.1rem', fontWeight: '700' }}
                    color="primary.dark"
                  >
                    No courses found
                  </Typography>
                )}
              </Box>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2} justifyContent="space-between" className="col-wrapper">
                <Stack sx={{ flex: 1.3 }} >
                  <Box
                    className="col "
                    sx={{
                      minHeight: '224px !important',
                      backgroundColor: (theme) =>
                        `${buttonColor || theme.palette.primary.main} !important`,
                      borderRadius: '0 !important',
                      border: `1px solid ${primaryColor} !important`,
                    }}
                  >
                    <Typography
                      variant="h2"
                      className="heading"
                      color={buttonFontColor || 'white'}
                      sx={{ fontSize: '1.5rem !important' }}
                    >
                      Average Salary{isWidget ? <br /> : ' '}
                      {careerNCoursesPopDetails?.salarySourcedFrom &&
                        `(${careerNCoursesPopDetails.salarySourcedFrom})`}
                    </Typography>
                    <Box
                      sx={{ minHeight: '140px !important' }}
                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <Stack>
                        <Typography
                          component="p"
                          color={buttonFontColor || 'secondary.main'}
                          style={{ textAlign: 'center', fontWeight: '700', fontSize: '2.15rem' }}
                        >
                          {careerNCoursesPopDetails?.averageSalary?.yearly != null &&
                          !Number.isNaN(Number(careerNCoursesPopDetails.averageSalary.yearly))
                            ? `£${new Intl.NumberFormat('en-GB', {
                                minimumFractionDigits: isWidget ? 0 : 2,
                                maximumFractionDigits: isWidget ? 0 : 2,
                              }).format(Number(careerNCoursesPopDetails.averageSalary.yearly))}`
                            : 'N/A'}
                        </Typography>

                        <Typography
                          component="p"
                          fontSize="18px !important"
                          fontWeight="600"
                          style={{ textAlign: 'center' }}
                          color={buttonFontColor || 'white'}
                        >
                          Per year
                        </Typography>
                      </Stack>

                      <Divider
                        sx={{
                          bgcolor: buttonFontColor || 'white',
                          width: '2px',
                          margin: '0 20px',
                          height: '60px',
                          alignSelf: 'center',
                        }}
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />

                      <Stack>
                        <Typography
                          component="p"
                          color={buttonFontColor || 'secondary.main'}
                          style={{ textAlign: 'center', fontWeight: '700', fontSize: '2.15rem' }}
                        >
                          {(() => {
                            const weeklySalary = Number(
                              careerNCoursesPopDetails?.averageSalary?.weekly
                            );
                            return !Number.isNaN(weeklySalary)
                              ? `£${new Intl.NumberFormat('en-GB', {
                                  minimumFractionDigits: isWidget ? 0 : 2,
                                  maximumFractionDigits: isWidget ? 0 : 2,
                                }).format(weeklySalary)}`
                              : 'N/A';
                          })()}
                        </Typography>

                        <Typography
                          component="p"
                          fontSize="18px !important"
                          fontWeight="600"
                          style={{ textAlign: 'center' }}
                          color={buttonFontColor || 'white'}
                        >
                          Per week
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: `${buttonColor} !important`,
                      borderRadius: '0 !important',
                      border: `1px solid ${primaryColor} !important`,
                      minHeight: '200px !important',
                    }}
                    className="col"
                  >
                    <Typography
                      variant="h2"
                      className="heading"
                      color={buttonFontColor || 'primary.dark'}
                      sx={{ fontSize: '1.5rem !important' }}
                    >
                      {isWidget ? (
                        <>
                          Average Expected
                          <br />
                          Weekly Hours
                          <InfoTip
                            text={
                              careerNCoursesPopDetails?.hoursSourcedFrom === 'AI'
                                ? 'AI-driven data'
                                : `Source: ${careerNCoursesPopDetails?.hoursSourcedFrom}`
                            }
                            label="Where the weekly hours come from"
                            color={buttonFontColor || 'primary.dark'}
                          />
                        </>
                      ) : (
                        <>
                          Average Expected Weekly Hours
                          <br />
                          {careerNCoursesPopDetails?.hoursSourcedFrom &&
                            `(${careerNCoursesPopDetails?.hoursSourcedFrom})`}
                        </>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        minHeight: '90px !important',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        component="p"
                        fontSize="2.15rem !important"
                        fontWeight={700}
                        color={buttonFontColor || 'secondary.main'}
                      >
                        {careerNCoursesPopDetails?.averageHours}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                <Box
                  className="col"
                  sx={{
                    flex: 1.7,
                    backgroundColor: 'white !important',
                    borderRadius: '0 !important',
                    border: `1px solid ${primaryColor} !important`,
                  }}
                >
                  <Typography
                    variant="h2"
                    textAlign="center"
                    className="heading"
                    color="primary.dark"
                    sx={{ fontSize: '1.5rem !important' }}
                  >
                    Typical Work Tasks
                  </Typography>
                  <ul>
                    {careerNCoursesPopDetails?.tasks &&
                    careerNCoursesPopDetails?.tasks?.length > 0 ? (
                      careerNCoursesPopDetails?.tasks?.map((item) => (
                        <li key={item}>
                          <Typography
                            component="p"
                            color="primary.dark"
                            textAlign="left"
                            fontSize="17px !important"
                          >
                            {item}
                          </Typography>
                        </li>
                      ))
                    ) : (
                      <Typography
                        component="p"
                        variant="body"
                        color="primary.dark"
                        textAlign="center"
                        sx={{ padding: '10px 20px' }}
                      >
                        No Data Available
                      </Typography>
                    )}
                  </ul>
                </Box>
              </Stack>
            </Box>
            <Box className="rowMax " sx={{ borderRadius: '0px !imoprtant' }}>
              <Box
                className="col skilldar"
                sx={{ padding: '30px 0 !important', borderRadius: '0px !imoprtant' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    borderRadius: '0px !imoprtant',
                  }}
                >
                  <Typography
                    variant="h2"
                    className="heading"
                    color="primary.dark"
                    sx={{ mr: 1, fontSize: '1.5rem !important' }}
                  >
                    Skilldar
                  </Typography>
                  {hasSkillsData(careerNCoursesPopDetails?.skilldarChartData) && (
                    <ZoomInIcon
                      onClick={HandleOpenRadarPopup}
                      fontSize="large"
                      sx={{ position: 'absolute', right: '20px', cursor: 'pointer' }}
                    />
                  )}
                </Box>
                <Box
                  className="flex radarWrapper"
                  sx={{ justifyContent: 'center', padding: '0px' }}
                >
                  {hasSkillsData(careerNCoursesPopDetails?.skilldarChartData) ? (
                    <RadarChartComponent
                    width="100%"
                    legend={false}
                    radarApiDetails={careerNCoursesPopDetails?.skilldarChartData}
                    height={370}
                    careersDetails
                    showlabels={false}
                    chartAnimation
                  />
                  ) : (
                    <Typography
                      component="p"
                      color="primary.dark"
                      sx={{ textAlign: 'center', fontWeight: 600, py: 6, px: 2 }}
                    >
                      No skills data for this career
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                className="col transfer-window"
                sx={{
                  backgroundColor: (theme) =>
                    `${buttonColor || theme.palette.primary.main} !important`,
                }}
              >
                <Typography
                  className="heading"
                  sx={{ marginBottom: '0px !important', fontSize: '1.5rem !important' }}
                  color={buttonFontColor || 'white'}
                  variant="h2"
                >
                  Transfer{isWidget ? <br /> : ' '}Window
                  {isWidget && (
                    <InfoTip
                      text={careerNCoursesPopDetails?.transferWindow?.description}
                      label="About the transfer window"
                      color={buttonFontColor || 'white'}
                    />
                  )}
                </Typography>
                <Typography
                  component="p"
                  fontSize="20px !important"
                  fontWeight="600"
                  sx={{ margin: '5px 0' }}
                  color={buttonFontColor || 'secondary.main'}
                >
                  {careerNCoursesPopDetails?.transferWindow?.duration}
                </Typography>
                {!isWidget && (
                  <Typography
                    sx={{ textAlign: 'center' }}
                    variant="medium"
                    color={buttonFontColor || 'white'}
                  >
                    {careerNCoursesPopDetails?.transferWindow?.description}
                  </Typography>
                )}
              </Box>
              <Box className="col-wrapper" sx={hideGrowth ? { display: 'none !important' } : undefined}>
                <Box className="col tab-wrapper">
                  <Typography
                    className="growth-heading heading"
                    variant="h2"
                    color="primary.dark"
                    sx={{ fontSize: '1.5rem !important' }}
                  >
                    Growth
                  </Typography>
                  <Typography variant="body1" color="primary.dark">
                    These charts show predicted demand by{' '}
                    {careerNCoursesPopDetails?.growthData?.regionalGrowth?.year}
                  </Typography>
                  <Box sx={{ width: '100%' }} className="popup-tab">
                    <Box sx={{ borderColor: 'divider', mx: 1 }}>
                      <Tabs
                        variant="fullWidth"
                        value={value}
                        onChange={handleChangeTab}
                        aria-label="custom styled tabs"
                        TabIndicatorProps={{ style: { display: 'none' } }} // remove default underline
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 1, // spacing between tabs
                          mt: 1,
                        }}
                      >
                        {[
                          'Growth',
                          'Qualification',
                          ...(regionData?.name && tabValue !== 0 ? [] : ['Work Type']),
                        ].map((label, index) => (
                          <Tab
                            key={label}
                            label={label}
                            {...a11yProps(index)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: '700',
                              borderRadius: '8px',
                              px: 2,
                              py: 0.5,
                              minHeight: '32px',
                              minWidth: 'auto',
                              bgcolor: value === index ? 'primary.main' : buttonColor,
                              color: value === index ? 'common.white' : 'primary.dark',
                              '&:hover': {
                                bgcolor: value === index ? 'primary.dark' : 'warning.light',
                              },
                            }}
                          />
                        ))}
                      </Tabs>
                    </Box>
                    <TabPanel value={value} index={0}>
                      <Box className="tab-content">
                        <Typography
                          component="p"
                          fontSize="18px !important"
                          textAlign="center"
                          color="black"
                        >
                          Overall Change{' '}
                          {careerNCoursesPopDetails?.growthData?.overallGrowth?.duration}
                        </Typography>
                        <Box className="stats-wrapper">
                          {/* <Typography color={`${careerNCoursesPopDetails?.growthData?.overallGrowth?.overallGrowthPercentage > 0 ? 'primary.success' : 'primary.error'}`}> */}
                          <Typography color="primary.dark">
                            {careerNCoursesPopDetails?.growthData?.overallGrowth
                              ?.overallGrowthPercentage > 0 ? (
                              <ArrowCircleUpOutlinedIcon sx={{ fontSize: '70px !important' }} />
                            ) : (
                              <ArrowCircleDownOutlinedIcon sx={{ fontSize: '70px !important' }} />
                            )}
                          </Typography>
                          {/* <Typography
                            color={buttonFontColor || 'secondary.main'}
                            fontWeight={700}
                            variant="h2"
                          >
                            {careerNCoursesPopDetails?.growthData?.overallGrowth
                              ?.overallGrowthPercentage > 0
                              ? '+'
                              : null}{' '}
                            {Number(
                              careerNCoursesPopDetails?.growthData?.overallGrowth
                                ?.overallGrowthNumber
                            ).toFixed(2)}
                          </Typography> */}
                          <Typography
                            color={buttonFontColor || 'primary.dark'}
                            textAlign="center"
                            fontWeight={700}
                            component="p"
                            fontSize="2.15rem !important"
                          >
                            {careerNCoursesPopDetails?.growthData?.overallGrowth
                              ?.overallGrowthPercentage > 0
                              ? '+'
                              : null}{' '}
                            {Number(
                              careerNCoursesPopDetails?.growthData?.overallGrowth
                                ?.overallGrowthPercentage
                            ).toFixed(2)}{' '}
                            %
                          </Typography>
                        </Box>
                      </Box>
                    </TabPanel>
                    {/* <TabPanel value={value} index={1}>
                                            <Box className='tab-content'>
                                                <Typography variant='h5' color='#b0b0b0' fontWeight={500}>Regional Growth {careerNCoursesPopDetails?.growthData?.regionalGrowth?.year}</Typography>
                                                <Box className="stats-wrapper">
                                                    <ApexCharts width='100%' type="bar" height={350} chartData={regionalChartData} />
                                                </Box>
                                            </Box>
                                        </TabPanel> */}
                    <TabPanel value={value} index={1}>
                      <Box className="tab-content">
                        <Typography
                          component="p"
                          fontSize="1.15rem !important"
                          color="#b0b0b0"
                          fontWeight={500}
                        >
                          Growth By Qualification Level{' '}
                          {careerNCoursesPopDetails?.growthData?.QualificationGrowth?.year}
                        </Typography>
                        <Box className="stats-wrapper">
                          <ApexCharts
                            width="100%"
                            type="bar"
                            height={350}
                            chartData={qualificationsChartData}
                          />
                        </Box>
                      </Box>
                    </TabPanel>
                    {(!regionData?.name || tabValue === 0) && (
                      <TabPanel value={value} index={2}>
                        <Box className="tab-content">
                          <Typography variant="h5" color="#b0b0b0" fontWeight={500}>
                            Predicted Workforce In{' '}
                            {careerNCoursesPopDetails?.growthData?.workTypeGrowth?.year}
                          </Typography>
                          <Box className="stats-wrapper">
                            <Box className="stats">
                              <Typography variant="medium" color="primary.light">
                                Full-time
                              </Typography>
                              <Typography variant="h2" color="primary.black">
                                {safeFormatNumber(
                                  careerNCoursesPopDetails?.growthData?.workTypeGrowth?.fullTime
                                )}
                              </Typography>
                            </Box>
                            <Box className="stats">
                              <Typography variant="medium" color="primary.light">
                                Part-time
                              </Typography>
                              <Typography variant="h2" color="primary.black">
                                {safeFormatNumber(
                                  careerNCoursesPopDetails?.growthData?.workTypeGrowth?.partTime
                                )}
                              </Typography>
                            </Box>
                            <Box className="stats">
                              <Typography variant="medium" color="primary.light">
                                Self-Employed
                              </Typography>
                              <Typography variant="h2" color="primary.black">
                                {safeFormatNumber(
                                  careerNCoursesPopDetails?.growthData?.workTypeGrowth
                                    ?.selfEmployeed
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TabPanel>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: '42%',
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              px: 2,
              textAlign: 'center',
            }}
          >
            {loadError ? (
              <>
                <Typography sx={{ color: 'white', fontWeight: 600 }}>
                  We couldn&apos;t load this career. Please try again.
                </Typography>
                <Button variant="contained" onClick={onRetry}>
                  Try again
                </Button>
              </>
            ) : (
              <CircularProgress sx={{ color: 'white' }} />
            )}
          </Box>
        )}
      </Box>
      {openCareerDetailsPopup && <CustomScrollbar targetRef={wrapperRef} scheme="onDark" />}
    </ModalComponent>
  );
}

DekstopPoup.propTypes = {
  children: PropTypes.node,
  careerNCoursesPopDetails: PropTypes.object,
  openCareerDetailsPopup: PropTypes.func,
  HandleCloseCareerDetailsPopup: PropTypes.func,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  onRetry: PropTypes.func,
  showComparePopup: PropTypes.func,
  addToCompare: PropTypes.func,
  compareCareersState: PropTypes.node,
  getSkillsReport: PropTypes.func,
  addToAnalytics: PropTypes.func,
  redirectUrl: PropTypes.func,
  currentNGoalCareerId: PropTypes.node,
  expanded: PropTypes.bool,
  HandleOpenRadarPopup: PropTypes.func,
  regionalChartData: PropTypes.object,
  setTabValue: PropTypes.func,
  tabValue: PropTypes.any,
  qualificationsChartData: PropTypes.object,
  regionData: PropTypes.object,
  handleChange: PropTypes.func,
  isWidget: PropTypes.bool,
  hideColleges: PropTypes.bool,
  hideGrowth: PropTypes.bool,
};
